import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Bot, Send, Loader2, MessageSquare, Plus, Trash2, X, Settings2,
  Sparkles, Megaphone, Calculator, TrendingUp, GraduationCap, User, Zap,
  FolderCog, Workflow, FolderOpen, Building2,
} from "lucide-react";
import { useSociete } from "../contexts/SocieteContext";
import { ProcessPanel } from "../components/ai/ProcessPanel";
import { SocieteFolderPanel } from "../components/ai/SocieteFolderPanel";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import ReactMarkdown from "react-markdown";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";

const AGENT_ICONS = {
  orchestrateur: Sparkles,
  marketing: Megaphone,
  comptabilite: Calculator,
  vente: TrendingUp,
  formation: GraduationCap,
  administratif: FolderCog,
};

const SUGGESTIONS = {
  orchestrateur: [
    "Prépare ma semaine : priorités commerciales, compta et marketing",
    "Quelles sont mes factures impayées et comment relancer ?",
    "Fais-moi une synthèse de l'activité de mon entreprise",
    "Comment améliorer ma rentabilité ce trimestre ?",
  ],
  marketing: [
    "Crée 3 posts LinkedIn pour promouvoir mes services",
    "Rédige une newsletter mensuelle pour mes clients",
    "Prépare une séquence de bienvenue en 3 emails",
    "Propose un calendrier éditorial pour le mois prochain",
  ],
  comptabilite: [
    "Analyse mes factures impayées et rédige les relances",
    "Prépare un plan de trésorerie prévisionnel",
    "Comment optimiser ma TVA ?",
    "Analyse mes dépenses et propose des économies",
  ],
  vente: [
    "Quels prospects dois-je relancer en priorité ?",
    "Analyse mon pipeline et mes prévisions de CA",
    "Prépare un devis pour une prestation de conseil",
    "Comment répondre à l'objection « c'est trop cher » ?",
  ],
  formation: [
    "Prépare une checklist pour mon audit Qualiopi",
    "Rédige un programme de formation conforme Qualiopi",
    "Génère un modèle de convention de formation",
    "Comment remplir mon BPF cette année ?",
  ],
  administratif: [
    "Quelles échéances administratives dois-je anticiper ce trimestre ?",
    "Fais l'inventaire des documents du dossier société et signale ce qui manque",
    "Rédige un courrier de résiliation de contrat fournisseur",
    "Prépare une checklist des obligations administratives annuelles",
  ],
};

function AgentConfigDialog({ api, agent, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(agent.enabled);
  const [functions, setFunctions] = useState(agent.functions || []);
  const [prompt, setPrompt] = useState(agent.system_prompt || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/ai/agents/${agent.type}`, { enabled, functions, system_prompt: prompt });
      toast.success("Configuration enregistrée");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="agent-config-dialog">
      <div className="bg-[#161020] border border-white/10 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Settings2 className="w-4 h-4" style={{ color: agent.color }} /> Configurer — {agent.name}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="config-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!agent.is_orchestrator && (
          <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-white/[0.03] border border-white/5">
            <span className="text-sm text-slate-300">Agent activé</span>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="toggle-agent-enabled" />
          </div>
        )}

        <p className="text-xs font-bold text-slate-500 mb-2">FONCTIONS</p>
        <div className="space-y-2 mb-4">
          {functions.map((fn, i) => (
            <div key={fn.id} className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
              <span className="text-xs text-slate-300">{fn.name}</span>
              <Switch
                checked={fn.enabled !== false}
                onCheckedChange={(v) => setFunctions(functions.map((f, j) => (j === i ? { ...f, enabled: v } : f)))}
                data-testid={`toggle-fn-${fn.id}`}
              />
            </div>
          ))}
        </div>

        <p className="text-xs font-bold text-slate-500 mb-2">PROMPT SYSTÈME</p>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={8}
          className="w-full bg-[#0b0811] border border-white/10 rounded-lg p-3 text-xs text-slate-300 focus:border-[#f7c935]/40 outline-none mb-4"
          data-testid="agent-prompt-input"
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-300 h-9 text-xs">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="save-agent-config">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AgentIAPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const isAdmin = ["admin", "superadmin", "tenant_admin"].includes(user?.role);
  const societeCtx = useSociete();
  const currentSociete = societeCtx?.currentSociete;
  const societeId = currentSociete?.societe_id || null;

  const [tab, setTab] = useState("chat");
  const [agents, setAgents] = useState([]);
  const [activeKey, setActiveKey] = useState("orchestrateur");
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [configAgent, setConfigAgent] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadAgents = async () => {
    try {
      const r = await api.get("/ai/agents");
      setAgents(r.data || []);
    } catch { setAgents([]); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadAgents();
    setSessionId("");
    setMessages([]);
  }, [societeId]);

  const loadSessions = async (agentKey) => {
    try {
      const r = await api.get(`/ai/sessions?agent_key=${agentKey}`);
      setSessions(r.data || []);
    } catch { setSessions([]); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions(activeKey); }, [activeKey, societeId]);

  const activeAgent = agents.find((a) => a.type === activeKey);

  const switchAgent = (key) => {
    setActiveKey(key);
    setSessionId("");
    setMessages([]);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const loadSession = async (sid) => {
    try {
      const r = await api.get(`/ai/sessions/${sid}`);
      setSessionId(sid);
      setMessages(r.data.messages || []);
      setShowSessions(false);
    } catch { /* ignore */ }
  };

  const deleteSession = async (sid) => {
    try {
      await api.delete(`/ai/sessions/${sid}`);
      if (sessionId === sid) { setSessionId(""); setMessages([]); }
      loadSessions(activeKey);
    } catch { /* ignore */ }
  };

  const newConversation = () => {
    setSessionId("");
    setMessages([]);
    setInput("");
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const sendMessage = async (overrideMsg) => {
    const msg = (overrideMsg || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { agent_key: activeKey, message: msg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((prev) => [...prev, {
        role: "assistant", content: r.data.response, timestamp: new Date().toISOString(),
        agent: r.data.routed_to, agent_name: r.data.agent_name, agent_color: r.data.agent_color, routed: r.data.routed,
      }]);
      loadSessions(activeKey);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: `Erreur : ${e.response?.data?.detail || "impossible de contacter l'agent"}`, timestamp: new Date().toISOString() }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const orchestrator = agents.find((a) => a.is_orchestrator);
  const thematics = agents.filter((a) => !a.is_orchestrator);
  const visibleThematics = isAdmin ? thematics : thematics.filter((a) => a.enabled);
  const color = activeAgent?.color || "#f7c935";
  const ActiveIcon = AGENT_ICONS[activeKey] || Bot;

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: "var(--bg-primary)", color: "var(--text-primary)" }} data-testid="agent-ia-page">
      <PageHeader
        icon={<Sparkles />}
        title="Agents IA"
        description="Votre équipe d'agents IA : un chef d'orchestre et 5 experts thématiques, autonomes grâce aux process"
        testId="agent-ia-header"
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { key: "chat", label: "Conversations", icon: MessageSquare },
          { key: "process", label: "Process", icon: Workflow },
          { key: "dossier", label: "Dossier société", icon: FolderOpen },
        ].map((t) => {
          const TabIcon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-colors ${tab === t.key ? "bg-[#f7c935] text-[#0b0811]" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
              data-testid={`ai-tab-${t.key}`}>
              <TabIcon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
        {currentSociete && (
          <span className="ml-auto flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg bg-white/5" style={{ color: "var(--text-muted)" }} data-testid="ai-societe-badge">
            <Building2 className="w-3.5 h-3.5 text-[#f7c935]" /> {currentSociete.name}
          </span>
        )}
      </div>

      {tab === "process" && <ProcessPanel api={api} agents={agents} societeId={societeId} />}
      {tab === "dossier" && <SocieteFolderPanel api={api} societeId={societeId} />}

      <div className={tab === "chat" ? "flex flex-col lg:flex-row gap-4" : "hidden"} style={{ minHeight: tab === "chat" ? "calc(100vh - 260px)" : undefined }}>
        {/* Agents rail */}
        <div className="w-full lg:w-72 flex-shrink-0 space-y-2" data-testid="agents-rail">
          {orchestrator && (
            <button
              onClick={() => switchAgent("orchestrateur")}
              className={`w-full text-left rounded-xl border p-4 transition-all ${activeKey === "orchestrateur" ? "border-[#f7c935]/60 bg-[#f7c935]/5" : "border-white/10 hover:border-[#f7c935]/30"}`}
              style={{ background: activeKey === "orchestrateur" ? undefined : "var(--bg-card)" }}
              data-testid="agent-card-orchestrateur"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#f7c935]/15">
                  <Sparkles className="w-5 h-5 text-[#f7c935]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#f7c935]">{orchestrator.name}</p>
                  <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>Chef d'orchestre</p>
                </div>
              </div>
              <p className="text-[11px] mt-2 leading-snug" style={{ color: "var(--text-muted)" }}>{orchestrator.description}</p>
            </button>
          )}

          <p className="text-[10px] font-bold pt-2 pb-1 px-1" style={{ color: "var(--text-dimmed)" }}>AGENTS THÉMATIQUES</p>
          {visibleThematics.map((a) => {
            const Icon = AGENT_ICONS[a.type] || Bot;
            return (
              <button
                key={a.type}
                onClick={() => a.enabled && switchAgent(a.type)}
                className={`w-full text-left rounded-xl border p-3 transition-all ${activeKey === a.type ? "bg-white/[0.04]" : "border-white/10 hover:bg-white/[0.02]"} ${!a.enabled ? "opacity-50 cursor-not-allowed" : ""}`}
                style={{ background: activeKey === a.type ? undefined : "var(--bg-card)", borderColor: activeKey === a.type ? `${a.color}80` : undefined }}
                data-testid={`agent-card-${a.type}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${a.color}18` }}>
                    <Icon className="w-4 h-4" style={{ color: a.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold truncate" style={{ color: activeKey === a.type ? a.color : "var(--text-primary)" }}>{a.name.replace("Agent ", "")}</p>
                    {!a.enabled && <span className="text-[9px] text-red-400 font-bold">Désactivé</span>}
                  </div>
                  {isAdmin && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setConfigAgent(a); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setConfigAgent(a); } }}
                      className="p-1 rounded hover:bg-white/10 text-slate-500"
                      data-testid={`config-agent-${a.type}`}
                    >
                      <Settings2 className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
          {/* Chat header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "var(--border-primary)", background: `${color}08` }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
              <ActiveIcon className="w-4 h-4" style={{ color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" style={{ color }}>{activeAgent?.name || "Agent IA"}</p>
              <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                {activeKey === "orchestrateur" ? "Route automatiquement vers le bon agent" : activeAgent?.description}
              </p>
            </div>
            {isAdmin && activeAgent && (
              <button onClick={() => setConfigAgent(activeAgent)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="config-active-agent-btn" title="Configurer">
                <Settings2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setShowSessions(!showSessions)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="sessions-toggle" title="Historique">
              <MessageSquare className="w-4 h-4" />
            </button>
            <button onClick={newConversation} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="new-conversation-btn" title="Nouvelle conversation">
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Sessions panel */}
          {showSessions && (
            <div className="border-b max-h-52 overflow-y-auto" style={{ borderColor: "var(--border-primary)", background: "var(--bg-primary)" }} data-testid="sessions-panel">
              {sessions.length === 0 ? (
                <p className="p-4 text-center text-xs" style={{ color: "var(--text-dimmed)" }}>Aucune conversation</p>
              ) : sessions.map((s) => (
                <div key={s.session_id} className={`group flex items-center gap-2 px-4 py-2.5 cursor-pointer border-b border-white/5 hover:bg-white/[0.03] ${sessionId === s.session_id ? "bg-white/[0.04]" : ""}`}>
                  <button onClick={() => loadSession(s.session_id)} className="flex-1 text-left min-w-0" data-testid={`session-${s.session_id}`}>
                    <p className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{s.title || "Conversation"}</p>
                    <p className="text-[9px]" style={{ color: "var(--text-dimmed)" }}>{new Date(s.updated_at || s.created_at).toLocaleString("fr-FR")}</p>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/10" data-testid={`delete-session-${s.session_id}`}>
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center pt-12">
                <ActiveIcon className="w-14 h-14 mx-auto mb-4" style={{ color, opacity: 0.35 }} />
                <h2 className="text-lg font-bold mb-2" style={{ color: "var(--text-primary)" }}>{activeAgent?.name || "Agent IA"}</h2>
                <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: "var(--text-muted)" }}>
                  {activeKey === "orchestrateur"
                    ? "Posez n'importe quelle question : je mobilise automatiquement le bon expert (Marketing, Comptabilité, Vente ou Formation)."
                    : activeAgent?.description}
                </p>
                <div className="flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
                  {(SUGGESTIONS[activeKey] || []).map((ex, i) => (
                    <button
                      key={ex}
                      onClick={() => sendMessage(ex)}
                      className="text-xs px-3 py-1.5 rounded-lg border transition-colors hover:bg-white/[0.03]"
                      style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }}
                      data-testid={`suggestion-${i}`}
                    >
                      <Zap className="w-3 h-3 inline mr-1.5" style={{ color: `${color}80` }} />{ex}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : ""}`}>
                {msg.role === "assistant" && (
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${msg.agent_color || color}20` }}>
                    <Bot className="w-3.5 h-3.5" style={{ color: msg.agent_color || color }} />
                  </div>
                )}
                <div className={`max-w-[78%] rounded-xl px-4 py-3 ${msg.role === "user" ? "bg-[#f7c935]/10 border border-[#f7c935]/20" : "bg-white/[0.03]"}`}>
                  {msg.role === "assistant" && msg.agent_name && (msg.routed || msg.agent !== activeKey) && (
                    <div className="flex items-center gap-1.5 mb-1.5" data-testid="routed-badge">
                      <Sparkles className="w-3 h-3" style={{ color: msg.agent_color || color }} />
                      <span className="text-[10px] font-bold" style={{ color: msg.agent_color || color }}>{msg.agent_name}</span>
                    </div>
                  )}
                  {msg.role === "assistant" ? (
                    <div className="prose prose-invert prose-sm max-w-none prose-headings:font-bold prose-h2:text-sm prose-h3:text-xs prose-p:text-sm prose-p:leading-relaxed prose-li:text-sm prose-strong:text-white prose-a:text-[#f7c935]">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: "var(--text-primary)" }}>{msg.content}</p>
                  )}
                </div>
                {msg.role === "user" && (
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#f7c935]/20">
                    <User className="w-3.5 h-3.5 text-[#f7c935]" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
                  <Bot className="w-3.5 h-3.5 animate-pulse" style={{ color }} />
                </div>
                <div className="bg-white/[0.03] rounded-xl px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: color, animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: color, animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: color, animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="p-4 border-t" style={{ borderColor: "var(--border-primary)" }} data-testid="chat-input-form">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={activeKey === "orchestrateur" ? "Posez votre question, je route vers le bon agent…" : "Posez votre question…"}
                className="flex-1 bg-transparent border rounded-lg px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#f7c935]/40"
                style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }}
                data-testid="chat-input"
              />
              <Button type="submit" disabled={loading || !input.trim()} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold rounded-lg px-4" data-testid="chat-send">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {configAgent && (
        <AgentConfigDialog api={api} agent={configAgent} onClose={() => setConfigAgent(null)} onSaved={loadAgents} />
      )}
    </div>
  );
}
